/**
 * Probation events for Career Timeline (admin + employee Profile).
 *
 * When probation was extended, we place a "Probation Extended" marker at the
 * original standard 3-month end (probation/hire start + 3 months) — the point
 * in the career story where the period was lengthened — and keep the upcoming
 * "Probation Ends" row on the new end date.
 */
import { addMonths } from 'date-fns';
import { formatDate, formatDaySpan, getCalendarDaysUntil, parseLocalDate, toLocalISODate } from './dateUtils';

/** Company default probation length before an extension (months). */
const STANDARD_PROBATION_MONTHS = 3;

/**
 * Append probation-related timeline events to `events` (mutates in place).
 *
 * @param {Array<object>} events
 * @param {object} opts
 * @param {string|null|undefined} opts.probEndDate - Stored / displayed end date
 * @param {boolean} opts.isProbation - employment_status === 'probation'
 * @param {boolean} [opts.probationExtended]
 * @param {string} [opts.extensionReason]
 * @param {string|null|undefined} opts.probationStartDate - probation_start or hire
 * @param {'admin'|'employee'} [opts.audience='admin']
 *   admin → reason + overdue; employee → softer copy, no overdue, no reason
 */
export function appendProbationTimelineEvents(events, opts) {
  const {
    probEndDate,
    isProbation,
    probationExtended = false,
    extensionReason = '',
    probationStartDate,
    audience = 'admin',
  } = opts || {};

  if (!probEndDate) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = parseLocalDate(probEndDate);
  if (!endDate) return;
  endDate.setHours(0, 0, 0, 0);

  const start = probationStartDate ? parseLocalDate(probationStartDate) : null;

  // Extended marker at the original standard end (start + 3 months), when that
  // date is strictly before the current end — narrative: "period lengthened here".
  if (probationExtended && start) {
    const originalEnd = addMonths(start, STANDARD_PROBATION_MONTHS);
    originalEnd.setHours(0, 0, 0, 0);
    if (originalEnd < endDate) {
      const newEndLabel = formatDate(probEndDate);
      const reason = (extensionReason || '').trim();
      let subtitle;
      if (audience === 'admin') {
        subtitle = reason
          ? `${reason} · New end ${newEndLabel}`
          : `New end ${newEndLabel}`;
      } else {
        // Employee-facing: clear date change without internal HR reason categories
        subtitle = `New end date ${newEndLabel}`;
      }
      events.push({
        date: toLocalISODate(originalEnd),
        title: 'Probation Extended',
        type: 'extended',
        subtitle,
      });
    }
  }

  if (!isProbation && endDate <= today) {
    events.push({
      date: probEndDate,
      title: 'Probation Completed',
      type: 'milestone',
      subtitle: probationExtended
        ? (audience === 'admin'
          ? 'Extended probation confirmed'
          : 'Successfully confirmed after extension')
        : 'Successfully confirmed',
    });
    return;
  }

  if (isProbation && endDate > today) {
    const daysRemaining = getCalendarDaysUntil(probEndDate);
    const base = daysRemaining == null
      ? ''
      : `${formatDaySpan(daysRemaining, 'long')} remaining`;
    events.push({
      date: probEndDate,
      title: 'Probation Ends',
      type: 'upcoming',
      subtitle: probationExtended && base ? `Extended · ${base}` : (base || undefined),
    });
    return;
  }

  // Overdue is HR-only operational signal — never show on employee Profile.
  if (isProbation && endDate <= today && audience === 'admin') {
    const daysOverdue = getCalendarDaysUntil(probEndDate);
    const overdueAbs = daysOverdue == null ? null : Math.abs(daysOverdue);
    const span = overdueAbs === 0
      ? 'Due today'
      : overdueAbs == null
        ? null
        : `${formatDaySpan(overdueAbs, 'long')} past due`;
    events.push({
      date: probEndDate,
      title: 'Probation Overdue',
      type: 'overdue',
      subtitle: span ? `${span} — action required` : 'Action required',
    });
  }
}
